import {
  cancellationReasonSchema,
  type BoardCaregiver,
  type BulkVisitJob,
  type BulkVisitJobStarted,
  type CancellationReason,
  type ClientList,
} from '@homecare/contracts';
import { addDays, toLocalDate } from '@homecare/domain';
import {
  Button,
  EmptyState,
  Field,
  Modal,
  Notice,
  SelectField,
  Spinner,
  StatusPill,
} from '@homecare/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';

import { ErrorNote } from '../../components/primitives';
import { useApi } from '../../lib/api-hooks';
import { dayText, shiftWeek } from '../../lib/schedule-time';
import { useUnsavedChangesGuard } from '../../lib/unsaved-changes';

/**
 * Bulk changes to the schedule (§14.6): reassign a caregiver's visits, cancel
 * a client's for a stretch, copy a week forward.
 *
 * Three steps, and the second is the point: choose, **preview** — every visit
 * judged by the same fifteen rules a single booking meets, each refused one
 * with the rule's words — then commit. The commit is all or nothing: if the
 * schedule moved since the preview, nothing is changed and the screen says
 * what moved.
 */

type Operation = 'REASSIGN' | 'CANCEL' | 'COPY_WEEK';

const OPERATIONS: { value: Operation; label: string; hint: string }[] = [
  {
    value: 'REASSIGN',
    label: 'Move a caregiver’s visits',
    hint: 'Leave, termination, a long absence: their visits go to someone else, or stay open for the board.',
  },
  {
    value: 'CANCEL',
    label: 'Cancel a client’s visits',
    hint: 'A hospital stay or a family holiday: every visit in the dates, cancelled with one reason.',
  },
  {
    value: 'COPY_WEEK',
    label: 'Copy a week forward',
    hint: 'One-off visits repeated a week later at the same local times. Visits from a shift pattern repeat by themselves.',
  },
];

const CANCEL_WORDS: Record<CancellationReason, string> = {
  CLIENT_REQUEST: 'The client asked to cancel',
  CLIENT_HOSPITALISED: 'The client is in hospital',
  CLIENT_UNAVAILABLE: 'The client is not available',
  SERVICE_ENDED: 'The service has ended',
  AGENCY_ERROR: 'Booked in error',
  WEATHER: 'Weather',
  OTHER: 'Something else',
};

const OUTCOME: Record<
  BulkVisitJob['rows'][number]['outcome'],
  { tone: 'scheduled' | 'critical' | 'inactive' | 'verified'; label: string }
> = {
  READY: { tone: 'scheduled', label: 'Ready' },
  REFUSED: { tone: 'critical', label: 'Refused' },
  SKIPPED: { tone: 'inactive', label: 'Skipped' },
  DONE: { tone: 'verified', label: 'Done' },
};

export function BulkChanges({
  open,
  onClose,
  weekStart,
  caregivers,
}: {
  open: boolean;
  onClose: () => void;
  weekStart: string;
  caregivers: readonly BoardCaregiver[];
}): ReactNode {
  return (
    <Modal
      open={open}
      onOpenChange={(next) => (next ? undefined : onClose())}
      title="Bulk changes"
      description="Preview first: every visit is checked against the same rules as a single booking. Nothing changes until you commit."
      className="max-h-[calc(100vh-4rem)] w-[min(52rem,calc(100vw-2rem))] overflow-y-auto"
    >
      {open && <BulkFlow weekStart={weekStart} caregivers={caregivers} onClose={onClose} />}
    </Modal>
  );
}

function BulkFlow({
  weekStart,
  caregivers,
  onClose,
}: {
  weekStart: string;
  caregivers: readonly BoardCaregiver[];
  onClose: () => void;
}): ReactNode {
  const api = useApi();
  const queryClient = useQueryClient();
  const [operation, setOperation] = useState<Operation>('REASSIGN');
  const [caregiverId, setCaregiverId] = useState('');
  const [toCaregiverId, setToCaregiverId] = useState('');
  const [clientId, setClientId] = useState('');
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState(weekStart);
  const [to, setTo] = useState<string>(addDays(toLocalDate(weekStart), 6));
  const [reasonCode, setReasonCode] = useState<CancellationReason | ''>('');
  const [note, setNote] = useState('');
  const [fromWeekOf, setFromWeekOf] = useState(weekStart);
  const [toWeekOf, setToWeekOf] = useState(shiftWeek(weekStart, 1));
  const [jobId, setJobId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);

  const clients = useQuery({
    queryKey: ['clients', 'bulk-search', search],
    queryFn: () =>
      api.get<ClientList>(
        `/v1/clients?pageSize=25${search.trim() ? `&search=${encodeURIComponent(search.trim())}` : ''}`,
      ),
    enabled: operation === 'CANCEL',
  });

  const job = useQuery({
    queryKey: ['schedule', 'bulk', jobId],
    queryFn: () => api.get<BulkVisitJob>(`/v1/visits/bulk/${jobId ?? ''}`),
    enabled: jobId !== null,
    // The job runs in the background; look again until it settles.
    refetchInterval: (query) =>
      query.state.data && !['PREVIEWING', 'COMMITTING'].includes(query.state.data.status)
        ? false
        : 1_000,
  });
  const data = job.data;
  useUnsavedChangesGuard(data?.status === 'READY' && data.counts.ready > 0);

  const body = (): Record<string, unknown> | null => {
    if (operation === 'REASSIGN') {
      return caregiverId
        ? { operation, caregiverId, toCaregiverId: toCaregiverId || null, from, to }
        : null;
    }
    if (operation === 'CANCEL') {
      return clientId && reasonCode
        ? { operation, clientId, from, to, reasonCode, note: note.trim() || null }
        : null;
    }
    return { operation, fromWeekOf, toWeekOf };
  };

  const start = async (): Promise<void> => {
    const payload = body();
    if (!payload) return;
    setBusy(true);
    setFailure(null);
    try {
      const started = await api.send<BulkVisitJobStarted>('POST', '/v1/visits/bulk', payload);
      setJobId(started.jobId);
    } catch (error) {
      setFailure(error);
    } finally {
      setBusy(false);
    }
  };

  const act = async (verb: 'commit' | 'discard'): Promise<void> => {
    if (!jobId) return;
    setBusy(true);
    setFailure(null);
    try {
      // Written out, not interpolated: each route is named where it is called.
      if (verb === 'commit') await api.send('POST', `/v1/visits/bulk/${jobId}/commit`);
      else await api.send('POST', `/v1/visits/bulk/${jobId}/discard`);
      if (verb === 'discard') {
        setJobId(null);
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ['schedule', 'bulk', jobId] });
    } catch (error) {
      setFailure(error);
    } finally {
      setBusy(false);
    }
  };

  // Once committed, the board behind this dialog is out of date.
  const committed = data?.status === 'COMMITTED';
  useEffect(() => {
    if (committed) void queryClient.invalidateQueries({ queryKey: ['schedule'] });
  }, [committed, queryClient]);

  // -- Choosing ---------------------------------------------------------------
  if (!jobId) {
    const ready = body() !== null;
    const chosen = OPERATIONS.find((entry) => entry.value === operation)!;
    return (
      <div className="grid gap-4" data-testid="bulk-form">
        <ErrorNote error={failure} nextStep="Check the choices, then preview again." />
        <SelectField
          label="What to change"
          hint={chosen.hint}
          value={operation}
          onChange={(event) => setOperation(event.target.value as Operation)}
        >
          {OPERATIONS.map((entry) => (
            <option key={entry.value} value={entry.value}>
              {entry.label}
            </option>
          ))}
        </SelectField>

        {operation === 'REASSIGN' && (
          <div className="grid gap-3 sm:grid-cols-2">
            <SelectField
              label="Whose visits"
              value={caregiverId}
              onChange={(event) => setCaregiverId(event.target.value)}
            >
              <option value="">Choose a caregiver</option>
              {caregivers.map((caregiver) => (
                <option key={caregiver.id} value={caregiver.id}>
                  {caregiver.name}
                </option>
              ))}
            </SelectField>
            <SelectField
              label="Give them to"
              value={toCaregiverId}
              onChange={(event) => setToCaregiverId(event.target.value)}
            >
              <option value="">Nobody — leave them open</option>
              {caregivers
                .filter((caregiver) => caregiver.id !== caregiverId)
                .map((caregiver) => (
                  <option key={caregiver.id} value={caregiver.id}>
                    {caregiver.name}
                  </option>
                ))}
            </SelectField>
          </div>
        )}

        {operation === 'CANCEL' && (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field
                label="Find a client"
                hint="A name or an MRN."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
              <SelectField
                label="Client"
                value={clientId}
                onChange={(event) => setClientId(event.target.value)}
              >
                <option value="">
                  {clients.isPending ? 'Loading clients…' : 'Choose a client'}
                </option>
                {(clients.data?.clients ?? []).map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.preferredName ?? client.firstName} {client.lastName} · {client.mrn}
                  </option>
                ))}
              </SelectField>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <SelectField
                label="Why"
                value={reasonCode}
                onChange={(event) => setReasonCode(event.target.value as CancellationReason)}
              >
                <option value="">Choose a reason</option>
                {cancellationReasonSchema.options.map((code) => (
                  <option key={code} value={code}>
                    {CANCEL_WORDS[code]}
                  </option>
                ))}
              </SelectField>
              <Field
                label="Note (optional)"
                value={note}
                onChange={(event) => setNote(event.target.value)}
              />
            </div>
          </>
        )}

        {operation !== 'COPY_WEEK' ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label="From"
              type="date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
            />
            <Field
              label="To"
              type="date"
              value={to}
              onChange={(event) => setTo(event.target.value)}
            />
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label="Copy the week of"
              type="date"
              value={fromWeekOf}
              onChange={(event) => setFromWeekOf(event.target.value)}
            />
            <Field
              label="Into the week of"
              type="date"
              value={toWeekOf}
              onChange={(event) => setToWeekOf(event.target.value)}
            />
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          {ready ? (
            <Button pending={busy} onClick={() => void start()}>
              Preview
            </Button>
          ) : (
            <Button disabled disabledReason="Choose who or what to change first.">
              Preview
            </Button>
          )}
        </div>
      </div>
    );
  }

  // -- The report -------------------------------------------------------------
  return (
    <div className="grid gap-4" data-testid="bulk-report">
      <ErrorNote error={failure ?? job.error} nextStep="Close this, and open the board again." />
      {!data || data.status === 'PREVIEWING' || data.status === 'COMMITTING' ? (
        <p className="text-ink-2 flex items-center gap-2 text-sm" role="status">
          <Spinner />{' '}
          {data?.status === 'COMMITTING'
            ? 'Committing every change at once…'
            : 'Checking every visit against the rules…'}
        </p>
      ) : (
        <>
          {data.status === 'COMMITTED' && (
            <Notice
              tone="verified"
              title={`Done: ${data.counts.done} visit${data.counts.done === 1 ? '' : 's'} changed.`}
            >
              The board shows them now. Each one’s history names you and this change.
            </Notice>
          )}
          {data.status === 'FAILED' && (
            <Notice tone="critical" title="Nothing was changed">
              {data.failure}
            </Notice>
          )}
          {data.status === 'READY' && (
            <Notice
              tone={data.counts.refused > 0 ? 'warning' : 'scheduled'}
              title={`${data.counts.ready} ready, ${data.counts.refused} refused, ${data.counts.skipped} skipped`}
            >
              Commit makes every ready change at once — or none, if the schedule has moved since
              this preview. Refused visits are not touched; handle them one at a time from the
              board.
            </Notice>
          )}

          {data.rows.length === 0 ? (
            <EmptyState
              title="No visits match"
              explanation="Nothing in those dates fits — no future, scheduled visits for that caregiver or client, or no one-off visits in that week."
            />
          ) : (
            <ul className="grid gap-1" aria-label="Each visit, and what happens to it">
              {data.rows.map((row) => (
                <li
                  key={row.key}
                  className="border-line grid gap-0.5 rounded-md border px-3 py-2 text-sm"
                >
                  <span className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-ink font-medium">
                      {dayText(row.localDate)}, {row.localStartTime}–{row.localEndTime} ·{' '}
                      {row.clientName}
                    </span>
                    <StatusPill tone={OUTCOME[row.outcome].tone}>
                      {OUTCOME[row.outcome].label}
                    </StatusPill>
                  </span>
                  <span className="text-ink-2">{row.action}</span>
                  {row.reasons.map((reason) => (
                    <span key={reason} className="text-critical text-xs">
                      {reason}
                    </span>
                  ))}
                  {row.warnings.map((warning) => (
                    <span key={warning} className="text-warning text-xs">
                      {warning}
                    </span>
                  ))}
                </li>
              ))}
            </ul>
          )}

          <div className="flex justify-end gap-2">
            {data.status === 'READY' || data.status === 'FAILED' ? (
              <Button variant="ghost" pending={busy} onClick={() => void act('discard')}>
                {data.status === 'FAILED' ? 'Start again' : 'Discard'}
              </Button>
            ) : (
              <Button variant="ghost" onClick={onClose}>
                Close
              </Button>
            )}
            {data.status === 'READY' &&
              (data.counts.ready > 0 ? (
                <Button pending={busy} onClick={() => void act('commit')}>
                  Commit {data.counts.ready} change{data.counts.ready === 1 ? '' : 's'}
                </Button>
              ) : (
                <Button disabled disabledReason="Nothing is ready to change.">
                  Commit
                </Button>
              ))}
          </div>
        </>
      )}
    </div>
  );
}
